<template>
  <div>
    <Head title="UIDs" />
    <div v-if="show" class="flex items-center justify-between mb-8 max-w-3xl bg-green-500 rounded">
      <div class="flex items-center">
        <svg class="flex-shrink-0 ml-4 mr-2 w-4 h-4 fill-white" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><polygon points="0 11 2 9 7 14 18 3 20 5 7 18" /></svg>
        <div class="py-4 text-white text-sm font-medium">{{ msgSuccess }}</div>
      </div>
      <button type="button" class="group mr-2 p-2" @click="show = false">
        <svg class="block w-2 h-2 fill-green-800 group-hover:fill-white" xmlns="http://www.w3.org/2000/svg" width="235.908" height="235.908" viewBox="278.046 126.846 235.908 235.908"><path d="M506.784 134.017c-9.56-9.56-25.06-9.56-34.62 0L396 210.18l-76.164-76.164c-9.56-9.56-25.06-9.56-34.62 0-9.56 9.56-9.56 25.06 0 34.62L361.38 244.8l-76.164 76.165c-9.56 9.56-9.56 25.06 0 34.62 9.56 9.56 25.06 9.56 34.62 0L396 279.42l76.164 76.165c9.56 9.56 25.06 9.56 34.62 0 9.56-9.56 9.56-25.06 0-34.62L430.62 244.8l76.164-76.163c9.56-9.56 9.56-25.06 0-34.62z" /></svg>
      </button>
    </div>
    <h1 class="mb-8 text-3xl font-bold">UIDs</h1>
    <div class="flex items-center justify-between mb-6">
      <search-with-date-range v-model="form.search" class="mr-0 w-full max-w-md mr-3"/>
      <search-dropdown v-model="form.skuType" :skuTypes="skuTypes" class="mr-0 w-full max-w-md mr-3"/>
      <date-range v-model="form.dateValue" class="mr-0 w-full max-w-md" @reset="reset"/>
      <button class="btn-orange text-white-400 hover:text-white-600" @click="searchUids">Search</button>
      <Link class="btn-orange hidden" href="/items/create">
        <span>Generate</span>
        <span class="hidden md:inline">&nbsp;UIDs</span>
      </Link>
    </div>
    <div class="flex items-center justify-between mb-6">
      <a
        v-if="(items.data.length > 0 && url())"
        class="btn-orange px-3 py-2 text-xs cursor-pointer"
        :href="`/items/download${url()}`"
        download
        target="__blank"
      >
        <span>Download</span>
      </a>
    </div>
    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <th class="pb-3 pt-3 px-3">UID</th>
          <th class="pb-3 pt-3 px-3">Part Number</th>
          <th class="pb-3 pt-3 px-3">Part Description</th>
          <th class="pb-3 pt-3 px-3">Category</th>
          <th class="pb-3 pt-3 px-3">Quantity Received</th>
          <th class="pb-3 pt-3 px-3">Number</th>
          <th class="pb-3 pt-3 px-3">Status</th>
          <th class="pb-3 pt-3 px-3">By</th>
          <th class="pb-3 pt-3 px-3">Created At</th>
        </tr>
        <tr v-for="item in items.data" :key="item.id" class="hover:bg-orange-100 focus-within:bg-gray-100">
          <td class="border-t">
            <Link class="flex items-center px-3 py-3 focus:text-orange-500" :href="`/items/${item.id}/movements`">
              {{ item.name }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3" :href="`/items/${item.id}/movements`" tabindex="-1">
              {{ item.sku }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3" :href="`/items/${item.id}/movements`" tabindex="-1">
              {{ item.description }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3" :href="`/items/${item.id}/movements`" tabindex="-1">
              {{ item.category }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3" :href="`/items/${item.id}/movements`" tabindex="-1">
              {{ item.quantity }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-3 py-3" :href="`/items/${item.id}/movements`" tabindex="-1">
              {{ item.number }}
            </Link>
          </td>
          <td class="w-px border-t">
            <Link class="flex items-center px-3" :href="`/items/${item.id}/movements`" tabindex="-1">
              {{ item.status }}
            </Link>
          </td>
          <td class="w-px border-t">
            <Link class="flex items-center px-3" :href="`/items/${item.id}/movements`" tabindex="-1">
              {{ item.user }}
            </Link>
          </td>
          <td class="w-px border-t">
            <Link class="flex items-center px-3" :href="`/items/${item.id}/movements`" tabindex="-1">
              {{ item.created_on }}
            </Link>
          </td>
        </tr>
        <tr v-if="items.data.length === 0">
          <td class="px-3 py-3 border-t" colspan="9">No data found</td>
        </tr>
      </table>
    </div>
    <pagination class="mt-6" :links="items.links" />
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
//import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import SearchWithDateRange from '@/Shared/SearchWithDateRange'
import SearchDropdown from '@/Shared/SearchDropdown'
import DateRange from '@/Shared/DateRange'
import Pagination from '@/Shared/Pagination'

export default {
  components: {
    Head,
    //Icon,
    Link,
    SearchWithDateRange,
    SearchDropdown,
    DateRange,
    Pagination,
  },
  layout: Layout,
  props: {
    filters: Object,
    items: Object,
    skuTypes: Object,
  },
  data() {
    return {
      form: {
        search: this.filters.search,
        dateValue: this.filters.dateValue,
        skuType: this.filters.skuType,
      },
      show: false,
      msgSuccess: 'UIDs have been generated',
    }
  },
  //watch: {
  //  form: {
  //    deep: true,
  //    handler: throttle(function () {
  //      this.$inertia.get('/sku-items', pickBy(this.form), { preserveState: true })
  //    }, 150),
  //  },
  //},
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    searchUids() {
      this.$inertia.get('/sku-items', pickBy(this.form), { preserveState: true })
    },
    url() {
      let query = '';
      if (this.form.search) {
        query += `search=${this.form.search}`;
      }
      if (this.form.skuType) {
        if (query) {
          query += '&';
        }
        query += `skuType=${this.form.skuType}`;
      }
      if (this.form.dateValue) {
        if (query) {
          query += '&';
        }
        query += `dateValue=${this.form.dateValue}`;
      }
      if (query) {
        query = `?${query}`;
      }
      return query;
    },
  },
}
</script>
